import { InjectionToken } from '@angular/core';

/**
 * Enumeration of possible money protection insurance status values.
 */
export enum MoneyProtectionStatusEnum {
  Yes = 'yes',
  No = 'no',
  NA = 'n/a',
}

/**
 * Configuration for money protection status message text.
 *
 * @example
 *
 * ```typescript
 * const statusText: MoneyProtectionStatusTexts = { message: 'is insured by the XYZ.' };
 * ```
 */
export interface MoneyProtectionStatusTexts {
  message: string;
}

/**
 * Configuration object for money protection status component.
 */
export interface MoneyProtectionStatusConfig {
  messagePerStatus: {
    [MoneyProtectionStatusEnum.Yes]?: MoneyProtectionStatusTexts;
    [MoneyProtectionStatusEnum.No]?: MoneyProtectionStatusTexts;
    [MoneyProtectionStatusEnum.NA]?: MoneyProtectionStatusTexts;
  };
  disclaimer?: string;
}
/**
 * Injection token used to provide static money protection status component configuration.
 *
 * @example
 *
 * ```typescript
 * providers: [
 *   {
 *     provide: BB_MONEY_PROTECTION_STATUS_CONFIG_TOKEN,
 *     useValue: config,
 *   },
 * ]
 * ```
 */
export const BB_MONEY_PROTECTION_STATUS_CONFIG_TOKEN = new InjectionToken<MoneyProtectionStatusConfig>(
  'BB_MONEY_PROTECTION_STATUS_CONFIGURATION_INJECTION_TOKEN',
);
/**
 * Injection token used to provide static banner component configuration.
 *
 * @example
 *
 * ```typescript
 * providers: [
 *   {
 *     provide: BB_MONEY_PROTECTION_BANNER_CONFIG_TOKEN,
 *     useValue: MoneyProtectionSampleBannerShowcaseComponent,
 *   },
 * ]
 * ```
 *
 * @usageNotes
 *
 * Create a component that will contain you banner code and responsiveness logic.
 *
 * ```typescript
 * @Component({
 *   selector: 'bb-money-protection-sample-banner',
 *   template: `
 *     <span>Money Protection banner</span>
 *   `,
 * })
 * export class MoneyProtectionSampleBannerShowcaseComponent {}
 * ```
 *
 * Provide this component through providers at application level.
 *
 * ```typescript
 * providers: [
 *   {
 *     provide: BB_MONEY_PROTECTION_BANNER_CONFIG_TOKEN,
 *     useValue: MoneyProtectionSampleBannerShowcaseComponent,
 *   },
 * ]
 * ```
 */
export const BB_MONEY_PROTECTION_BANNER_CONFIG_TOKEN = new InjectionToken<string>(
  'BB_MONEY_PROTECTION_BANNER_CONFIGURATION_INJECTION_TOKEN',
  {
    providedIn: 'root',
    factory: () => '',
  },
);
